import u from '@/util'
import data from '@/data'
import { t } from '@/i18n'
import { compareRoleRank, cellTypesByRole } from '@/global'

const state = {
  // from: '2018-01-01',
  from: u.dateAdd(u.today(), -30),
  to: u.today(),
  activeTab: 1,
  personStats: [],
  cellStats: [],
  sectionStats: [],
  summary: '',
  total: {}
}

const getters = {
  role: (state, getters, rootState) => rootState.auth.user && rootState.auth.user.role || 'G',
  types: (state, getters, rootState) => cellTypesByRole(getters.role),
  cellHeaders: (state, getters, rootState) => {
    return (compareRoleRank(
      getters.role, 'L') > 0 ? [
        { text: t('Section'), value: 'parent', sortable: false },
        // { text: t('Cell'), value: 'cell', sortable: false },
        { text: t('Cell'), value: 'desc', sortable: false }] : [])
      .concat([
        { text: t('Number of meetings'), value: 'meetingCount', sortable: false },
        { text: t('Number of persons'), value: 'personCount', sortable: false },
        { text: t('Average frequency'), value: 'averageFrequency', sortable: false },
        { text: t('Average person count'), value: 'averagePersonCount', sortable: false },
        { text: t('Meeting span'), value: 'meetingSpan', sortable: false }
      ])
  },
  sectionHeaders: (state, getters) =>
    getters.cellHeaders.filter(_ => !['cell', 'desc'].includes(_.value)),
  personHeaders: (state, getters, rootState) => {
    return (compareRoleRank(
      getters.role, 'L') > 0 ? [
        { text: t('Section'), value: 'parent', sortable: false },
        // { text: t('Cell'), value: 'cell', sortable: false },
        { text: t('Cell'), value: 'desc', sortable: false }] : [])
      .concat([
        { text: t('Person'), value: 'person', align: 'left', sortable: false },
        { text: t('Sum of attendance'), value: 'presentSum', sortable: false },
        { text: t('Frequency'), value: 'frequency', sortable: false }
      ])
      .concat(u.weeks(state.from, state.to).map((v, i) => ({
        text: u.formatWeek(v),
        align: 'center',
        sortable: false,
        value: i
      })))
  }
}

const mutations = {
  personStats (state, payload) {
    state.personStats = payload
  },
  cellStats (state, payload) {
    state.cellStats = payload
  },
  sectionStats (state, payload) {
    state.sectionStats = payload
  },
  summary (state, payload) {
    state.summary = payload
  },
  total (state, payload) {
    state.total = payload
  },
  dates (state, payload) {
    const { from, to } = payload
    state.from = from
    state.to = to
  },
  activeTab (state, index) {
    state.activeTab = index
  }
}

const present = '&#10004;'
const absent = ''
// const absent = '&mdash;'
const roleSymbolString = 'A AL ASL G L M N SL SP P V'
const roleSymbols = roleSymbolString.split(' ')
const roleSymbolsLocale = t(roleSymbolString).split(' ')

function roleSymbol (role) {
  const i = roleSymbols.indexOf(role)
  return roleSymbolsLocale[i]
}

const actions = {
  async find (context, payload) {
    const { cellTypeName, period, startingPoint } = payload
    const cellType = cellTypeName === 'Open cell' ? 'O' : 'L'
    const d1 = new Date()
    const d2 = new Date()

    switch (startingPoint) {
      case 'Until now':
        const m = period === 'Month' ? 1 : period === 'Quarter' ? 3 : period === 'Half year' ? 6 : 12
        d1.setMonth(d1.getMonth() - m)
        break
      case 'Current':
        switch (period) {
          case 'Month':
            d1.setDate(1)
            break
          case 'Quarter':
            d1.setMonth((Math.floor(d1.getMonth() / 3)) * 3)
            d1.setDate(1)
            break
          case 'Half year':
            d1.setMonth((Math.floor(d1.getMonth() / 6)) * 6)
            d1.setDate(1)
            break
          case 'Year':
            d1.setMonth(0)
            d1.setDate(1)
            break
        }
        break
      case 'Previous':
        switch (period) {
          case 'Month':
            d1.setMonth(d1.getMonth() - 1)
            d1.setDate(1)
            d2.setDate(-1)
            break
          case 'Quarter':
            d1.setMonth((Math.floor(d1.getMonth() / 3) - 1) * 3)
            d1.setDate(1)
            d2.setMonth((Math.floor(d2.getMonth() / 3)) * 3)
            d2.setDate(-1)
            break
          case 'Half year':
            d1.setMonth((Math.floor(d1.getMonth() / 6) - 1) * 6)
            d1.setDate(1)
            d2.setMonth((Math.floor(d2.getMonth() / 6)) * 6)
            d2.setDate(-1)
            break
          case 'Year':
            d1.setFullYear(d1.getFullYear() - 1)
            d1.setMonth(0)
            d1.setDate(1)
            d2.setMonth(0)
            d2.setDate(-1)
            break
        }
        break
    }
    const from = u.formatDate(d1)
    const to = u.formatDate(d2)
    context.commit('dates', { from, to })

    const weeks = u.weeks(from, to)

    const role = context.rootState.auth.user.role
    const cells = (role === 'A' || role === 'P' ?
      context.rootState.cell.names : [context.rootState.auth.cell].concat(compareRoleRank(role, 'L') > 0 ? context.rootState.cell.item.cells : []))
      .filter(name => {
        const cell = context.rootState.cell.cells[name]
        return cellType === cell.type && !cell.archived
      })

    const cellStats = []
    const personStats = []

    let totalMeetingCount = 0
    let totalMemberFrequency = 0
    let totalPersonCount = 0
    let totalUniquePersonCount = 0
    let activeGroupCount = 0
    // (await data.assignments.findAll()) .filter(a => u.periodsOverlap(a.start, a.end, from, to))

    await Promise.all(cells.map(async cell => {
      const desc = context.rootState.cell.cells[cell].desc || cell
      const parent = context.rootState.cell.cells[cell].parent
      const parentDesc = (context.rootState.cell.cells[parent] || {}).desc || parent
      const meetings = await data.meetings2.findByCell(cell)
      if (!meetings) return
      const meetingDates = meetings.dates.filter(d => from <= d && d <= to)
      const meetingWeeks = meetingDates.map(d => u.week(d).getTime())
      const meetingCount = meetingDates.length
      totalMeetingCount += meetingCount
      if (meetingCount) activeGroupCount++

      // Person stats
      const allAssignments = (await data.assignments2.findByCell(cell) || []).map(a => a.person)
      allAssignments.sort((a, b) => a.localeCompare(b, 'pl'))
      const assignments = allAssignments
        .concat(meetingDates.map(d => Object.keys(meetings[d].attendance)).flat(2))
        .sort((a, b) => a.localeCompare(b, 'pl'))
        .filter((p, i, a) => p !== a[i - 1])

      let averagePersonCount = 0
      let presentMembers = 0
      let allMembers = 0

      if (meetingCount) {
        meetingDates.forEach(d => {
          const a = meetings[d].attendance || {}
          averagePersonCount += Object.keys(a).length
          presentMembers += Object.values(a).filter(role => !['G', 'V'].includes(role)).length
          allMembers += meetings[d].memberCount
        })
        averagePersonCount = (averagePersonCount / meetingCount).toFixed(1)
      }

      const averagePersonFrequency = allMembers ? presentMembers / allMembers : 0

      const cellStatsRecord = {
        parent: parentDesc,
        desc,
        meetingCount: meetingDates.length,
        personCount: assignments.length,
        averageFrequency: Math.floor(100 * averagePersonFrequency) + '%',
        averagePersonCount,
        meetingSpan: (meetingCount ? weeks.length / meetingCount : 0).toFixed(2),
        presentSum: 0
      }
      cellStats.push(cellStatsRecord)
      totalMemberFrequency += averagePersonFrequency
      totalPersonCount += parseFloat(cellStatsRecord.averagePersonCount)
      totalUniquePersonCount += cellStatsRecord.personCount

      Array.prototype.push.apply(personStats,
        // Create row for every assignment during the given period
        assignments.map(person => {
          let presentSum = 0
          // Create value for each column reprezenting a given week
          let attendance = weeks.map(w => {
            const i = meetingWeeks.indexOf(w.getTime())
            if (i === -1) return ''
            // totalAttendance += 1
            const attendance = meetings[meetingDates[i]].attendance || {}
            const isPresent = Object.keys(attendance).includes(person)
            if (isPresent) presentSum++
            return isPresent ? roleSymbol(attendance[person]) : absent
          })
          cellStatsRecord.presentSum += presentSum
          // totalPresentSum += presentSum
          let frequency = meetingCount ? Math.floor(100 * presentSum / meetingCount) + '%' : ''
          return {
            parent: parentDesc,
            desc,
            presentSum,
            frequency,
            person,
            ...attendance
          }
        }))
    }))
    // const totalCount = Object.keys(cellStats).reduce((acc, cur) => acc + cellStats[cur].personCount, 0)
    // const averageFrequency = Math.floor(100 * totalPresentSum / totalAttendance) + '%'
    personStats.sort(u.comparator('parent', 'cell'))
    context.commit('personStats', personStats)

    cellStats.sort(u.comparator('parent', 'cell'))
    context.commit('cellStats', cellStats)

    const sectionStats = []
    cellStats.forEach(_ => {
      let r = sectionStats.find(v => v.parent === _.parent)
      // if (!_.meetingCount) return
      if (!r) {
        r = {
          parent: (context.rootState.cell.cells[_.parent] || {}).desc || _.parent,
          cellCount: 0,
          meetingCount: 0,
          personCount: 0,
          averageFrequency: 0,
          averagePersonCount: 0,
          meetingSpan: 0,
          presentSum: 0
        }
        sectionStats.push(r)
      }
      r.cellCount += _.meetingCount ? 1 : 0
      r.meetingCount += _.meetingCount
      r.personCount += _.personCount
      r.averageFrequency += parseFloat(_.averageFrequency.slice(0, -1))
      r.averagePersonCount += parseFloat(_.averagePersonCount)
      r.meetingSpan += parseFloat(_.meetingSpan)
    })
    sectionStats.forEach(_ => {
      if (_.cellCount) {
        _.averageFrequency = Math.floor(_.averageFrequency / _.cellCount) + '%'
        _.averagePersonCount = (_.averagePersonCount / _.cellCount).toFixed(1)
        _.meetingSpan = (_.meetingSpan / _.cellCount).toFixed(2)
      }
      delete _.cellCount
    })

    sectionStats.sort(u.comparator('parent'))
    context.commit('sectionStats', sectionStats)

    // Summary

    const initialValue = () => Array(weeks.length).fill(0)
    const reducer = (acc, curr) => acc.map((v, i) => v + (curr[i] === present ? 1 : 0))
    const attended = personStats.reduce(reducer, initialValue()) // .map(v => v || '')
    // const reducer2 = (acc, curr) => acc.map((v, i) => v + (curr[i] === '' ? 0 : 1))
    // const divisible = personStats.reduce(reducer2, initialValue())
    // const attendance = attended.map((v, i) => (divisible[i] ? Math.floor(v / divisible[i] * 100) : 0))
    // const attendanceFormatted = attendance.map(v => v !== 0 ? v + '%' : '')
    // const attendanceSum = attendance.reduce((acc, cur) => acc + cur)
    // const totalAttendance = Math.floor(attendanceSum / totalCount) + '%'
    const blank = compareRoleRank(context.getters.role, 'L') > 0 ? [''] : []
    const summary = [t('Total')].concat(blank).concat([
      totalMeetingCount,
      totalUniquePersonCount,
      Math.floor((100 * totalMemberFrequency / activeGroupCount)) + '%',
      (totalPersonCount / activeGroupCount).toFixed(1),
      (activeGroupCount * weeks.length / totalMeetingCount).toFixed(1)
    ])
    context.commit('summary', summary)

    // Total
    const sum = attended.reduce((acc, curr) => acc + curr, 0)
    const count = attended.reduce((acc, curr) => curr ? acc + 1 : acc, 0)
    const average = (sum / weeks.length).toFixed(2)
    const frequency = (count ? weeks.length / count : 0).toFixed(2)
    context.commit('total', { sum, count, average, frequency })

    // Cell stats
  },

  copy (context) {
    let data
    switch (context.state.activeTab) {
      case 0:
        data = [context.getters.personHeaders.map(v => v.text)]
          .concat(Object.values(context.state.personStats).map(row => {
            const a = Object.values(row).map(v => v === absent ? '' : v)
            const person = a[a.length - 1]
            const frequency = a[a.length - 2]
            const presentSum = a[a.length - 3]
            // const cell = a[a.length - 5]
            const desc = a[a.length - 4]
            const parent = a[a.length - 6]

            if (compareRoleRank(context.getters.role, 'L') > 0) {
              a.splice(a.length - 6)
              a.unshift(parent, desc, person, presentSum, frequency)
            } else {
              a.splice(a.length - 3)
              a.unshift(person, presentSum, frequency)
            }
            return a
          }))
        break

      case 1:
        data = [context.getters.cellHeaders.map(v => v.text)]
          .concat(context.state.cellStats.map(
            r => Object.values(r).slice(0, -1).map(_ => ('' + _).replace('.', ','))))
        break

      case 2:
        data = [context.getters.sectionHeaders.map(v => v.text)]
          .concat(context.state.sectionStats.map(
            r => Object.values(r).slice(0, -1).map(_ => ('' + _).replace('.', ','))))
    }

    u.copyTextToClipboard(data.map(row => row.join('\t')).join('\n'))
    context.commit('snackbar', t('Copied to clipboard'), { root: true })
  }
}

export default { namespaced: true, state, getters, mutations, actions }
